import {
  AlertTriangle,
  Printer,
  RefreshCw,
  Settings,
  Wifi,
  WifiOff
} from 'lucide-react'
import { useEffect, useState } from 'react'

import ButtonGroup from '@/components/form/ButtonGroup'
import { usePrintActions, usePrintState } from '@/stores/usePrintStore'

const PrintSetup: React.FC = () => {
  const { loading, printer, printers, host, localhost, connect } =
    usePrintState()
  const { setPrinter, setHost, setLocalhost, loadPrinters } =
    usePrintActions()
  const [inputHost, setInputHost] = useState(host)
  const [connMode, setConnMode] = useState<string>()

  useEffect(() => {
    if (localhost) {
      setConnMode('localhost')
    } else {
      setConnMode('remote')
    }
  }, [localhost])

  const hancleLocalhostChange = (value: string) => {
    if (value === 'localhost') {
      setLocalhost(true)
    } else {
      setLocalhost(false)
    }
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* 标题栏 */}
      <div className="px-4 py-3 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Settings className="w-5 h-5 mr-2" />
          打印机设置
        </h3>
      </div>

      <div className="p-4 space-y-4">
        {/* 连接状态 */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-2">
            {connect ? (
              <Wifi className="w-5 h-5 text-green-500" />
            ) : (
              <WifiOff className="w-5 h-5 text-red-500" />
            )}
            <span className="text-sm font-medium text-gray-700">连接状态</span>
          </div>
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              connect
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}
          >
            {connect ? '已连接' : '未连接'}
          </span>
        </div>

        {/* 当前选择的打印机 */}
        {printer && (
          <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
            <div className="flex items-center space-x-2">
              <Printer className="w-5 h-5 text-indigo-600" />
              <div>
                <div className="text-sm font-medium text-indigo-900">
                  当前打印机
                </div>
                <div className="text-sm text-indigo-700">{printer}</div>
              </div>
            </div>
          </div>
        )}

        {/* 连接模式切换 */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              连接模式
            </label>
            <p className="text-xs text-gray-500">
              当前服务器: {localhost ? '127.0.0.1' : host || '未设置'}
            </p>
          </div>

          <div className="flex items-center space-x-4">
            <ButtonGroup
              options={[
                { key: 'localhost', label: '本地连接' },
                { key: 'remote', label: '远程服务器' }
              ]}
              value={connMode}
              onChange={(value) => {
                hancleLocalhostChange(value)
              }}
            />
          </div>
        </div>

        {/* 主机地址设置 */}
        {!localhost && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                打印服务器地址
              </label>
              <button
                onClick={() => setHost(inputHost)}
                disabled={loading && (inputHost === host || !inputHost.trim())}
                className="flex items-center space-x-1 px-3 py-1 text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
              >
                <RefreshCw
                  className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
                />
                <span>应用</span>
              </button>
            </div>

            <input
              type="text"
              value={inputHost}
              onChange={(e) => setInputHost(e.target.value)}
              placeholder="输入服务器IP地址，如: 192.168.1.100"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        )}

        {/* 打印机选择 */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              默认打印机
            </label>
            <button
              onClick={loadPrinters}
              disabled={loading}
              className="flex items-center space-x-1 px-3 py-1 text-sm text-indigo-600 hover:text-indigo-700 disabled:opacity-50"
            >
              <RefreshCw
                className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
              />
              <span>刷新</span>
            </button>
          </div>

          <select
            value={printer}
            onChange={(e) => setPrinter(e.target.value)}
            disabled={loading || printers.length === 0}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:text-gray-500"
          >
            {printers.length === 0 ? (
              <option value="">暂无可用打印机</option>
            ) : (
              printers.map((printerName) => (
                <option key={printerName} value={printerName}>
                  {printerName}
                </option>
              ))
            )}
          </select>

          {printers.length === 0 && !loading && (
            <p className="text-xs text-amber-600 flex items-center space-x-1">
              <AlertTriangle className="w-4 h-4" />
              <span>未检测到打印机，请检查连接或点击刷新</span>
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default PrintSetup
